// Receipts in words. The gateway records what it read back after every external action
// (server/gateway/actions/*): a sent message, an archive, calendar holds (`event_created` with a
// `hold` read-back), a booking (`event_created` with an `event` read-back) and the holds released
// after a booking (`holds_released`), the one-click unsubscribe POST (`unsubscribe_posted`,
// with the host and the HTTP status the sender's server answered, or `answered: false` when it
// never did) and the thread actions from the inbox views (`thread_archived`,
// `thread_unarchived`, `labels_changed`, each with the labels Gmail showed back). Pure: callers
// pass time options (tests pin them) and, for label ids, a name lookup.
import type { ReceiptView } from "@shared/api";
import { humanizeKey } from "@web/lib/humanize";
import { formatShortDateTime, type TimeFormatOptions } from "@web/lib/time";

export interface ReceiptWordOptions extends TimeFormatOptions {
  /** Label ids in words ("Label_12" → "Receipts"); ids stay as they are without it. */
  labelName?: (id: string) => string;
}

export interface ReceiptWords {
  title: string;
  /** Detail lines under the title (times held, invitees, …). */
  lines: string[];
  links: { href: string; label: string }[];
}

const TITLES: Record<string, string> = {
  email_sent: "Sent",
  send_reply: "Sent",
  forward: "Forwarded",
  email_forwarded: "Forwarded",
  archive: "Archived",
  thread_archived: "Archived",
  thread_unarchived: "Moved back to the inbox",
  label: "Labelled",
  thread_labelled: "Labelled",
  labels_changed: "Labels changed",
  holds_released: "Other held times released",
  unsubscribe_posted: "Unsubscribe request sent",
};

type Dict = Record<string, unknown>;
const str = (v: unknown): string | null => (typeof v === "string" && v ? v : null);
const num = (v: unknown): number => (typeof v === "number" && Number.isFinite(v) ? v : 0);
const isHttps = (v: string) => /^https:\/\//i.test(v);

function holdLines(readback: Dict, options: TimeFormatOptions): string[] {
  const events = Array.isArray(readback.events) ? (readback.events as Dict[]) : [];
  return events.map((e) => {
    const zone = str(e.tz) ? { ...options, timeZone: str(e.tz) ?? options.timeZone } : options;
    const when = str(e.start) ? formatShortDateTime(str(e.start), zone) : "a time";
    const detail = str(e.detail) ?? (e.state === "held" ? "Held" : String(e.state ?? ""));
    return `${when} · ${detail}`;
  });
}

function eventLines(readback: Dict, base: TimeFormatOptions): string[] {
  const lines: string[] = [];
  const options = str(readback.tz)
    ? { ...base, timeZone: str(readback.tz) ?? base.timeZone }
    : base;
  const start = str(readback.start);
  const end = str(readback.end);
  if (start) {
    const endTime = end
      ? new Intl.DateTimeFormat(options.locale, {
          hour: "numeric",
          minute: "2-digit",
          timeZone: options.timeZone,
        }).format(new Date(end))
      : null;
    lines.push(
      endTime
        ? `${formatShortDateTime(start, options)} – ${endTime}`
        : formatShortDateTime(start, options),
    );
  }
  const attendees = Array.isArray(readback.attendees)
    ? readback.attendees.filter((a): a is string => typeof a === "string")
    : [];
  if (attendees.length) lines.push(`Invited: ${attendees.join(", ")}`);
  if (readback.available === false)
    lines.push("Google accepted it but could not show it back yet.");
  return lines;
}

const THREAD_RECEIPTS = new Set(["thread_archived", "thread_unarchived", "labels_changed"]);

function idList(v: unknown): string[] {
  return Array.isArray(v) ? v.filter((x): x is string => typeof x === "string" && !!x) : [];
}

/** What Gmail showed back after an archive, an unarchive or a label change. */
function threadLines(kind: string, readback: Dict, name: (id: string) => string): string[] {
  const lines: string[] = [];
  if (readback.available === false) {
    lines.push("Gmail accepted it but could not show the thread back yet.");
    return lines;
  }
  if (kind === "thread_archived") {
    lines.push(
      readback.inInbox === false
        ? "Out of the inbox; still in All Mail and in search."
        : "Gmail still shows it in the inbox.",
    );
  } else if (kind === "thread_unarchived") {
    lines.push(
      readback.inInbox === true ? "Back in the inbox." : "Gmail does not show it in the inbox yet.",
    );
  } else {
    const added = idList(readback.added).map(name);
    const removed = idList(readback.removed).map(name);
    if (added.length) lines.push(`Added: ${added.join(", ")}.`);
    if (removed.length) lines.push(`Removed: ${removed.join(", ")}.`);
    if (readback.reached === false) lines.push("Gmail did not show the whole change yet.");
  }
  return lines;
}

export function receiptWords(
  receipt: Pick<ReceiptView, "kind" | "readback">,
  options: ReceiptWordOptions = {},
): ReceiptWords {
  const readback = (receipt.readback ?? {}) as Dict;
  if (THREAD_RECEIPTS.has(receipt.kind)) {
    return {
      title: TITLES[receipt.kind]!,
      lines: threadLines(receipt.kind, readback, options.labelName ?? ((id) => id)),
      links: [],
    };
  }
  if (receipt.kind === "event_created") {
    if (readback.kind === "hold")
      return {
        title: "Times held on your calendar",
        lines: holdLines(readback, options),
        links: [],
      };
    const links: ReceiptWords["links"] = [];
    const html = str(readback.htmlLink);
    const meet = str(readback.hangoutLink);
    if (html && isHttps(html)) links.push({ href: html, label: "Open in Google Calendar" });
    if (meet && isHttps(meet)) links.push({ href: meet, label: "Google Meet link" });
    return { title: "Meeting booked", lines: eventLines(readback, options), links };
  }
  if (receipt.kind === "unsubscribe_posted") {
    const host = str(readback.host) ?? "their server";
    const status = typeof readback.status === "number" ? readback.status : null;
    const lines =
      readback.answered === false || status === null
        ? [`${host} did not answer; their next email will tell.`]
        : status >= 200 && status < 300
          ? [`${host} answered HTTP ${status}.`]
          : [`${host} refused with HTTP ${status}.`];
    const redirects = num(readback.redirects);
    if (redirects) lines.push(`Followed ${redirects} redirect${redirects === 1 ? "" : "s"}.`);
    return { title: TITLES.unsubscribe_posted!, lines, links: [] };
  }
  if (receipt.kind === "holds_released") {
    const released = num(readback.releasedAlternatives);
    const replaced = num(readback.replacedByBooking);
    const lines: string[] = [];
    if (released)
      lines.push(`${released} other held ${released === 1 ? "time" : "times"} released.`);
    if (replaced) lines.push("The hold on the booked time was replaced by the booking.");
    return { title: TITLES.holds_released!, lines, links: [] };
  }
  const title = TITLES[receipt.kind] ?? humanizeKey(receipt.kind);
  return { title: title[0]!.toUpperCase() + title.slice(1), lines: [], links: [] };
}

const ISO_TAIL = /^([a-z_]+):(\d{4}-\d{2}-\d{2}T[^\s]+)$/i;
const EVENT_ID = /^[a-v0-9]{20,}$/;

/** A check's name in words: "held:2026-…" → "held Oct 6, 2:00 PM"; an event id → "hold …abcd". */
export function checkWords(name: string, options: TimeFormatOptions = {}): string {
  const dated = ISO_TAIL.exec(name);
  if (dated) return `${humanizeKey(dated[1]!)} ${formatShortDateTime(dated[2], options)}`;
  if (EVENT_ID.test(name)) return `hold …${name.slice(-4)}`;
  return humanizeKey(name);
}
